// ===== 점수 계산 및 알림 유틸리티 =====

import type { DailyCheckin, AlertType } from '../services/types';
import { CHECKIN_QUESTIONS } from '../services/types';

/**
 * 점수 배열의 평균을 소수점 1자리로 반올림하여 반환
 */
export function calculateAverage(scores: number[]): number {
  if (scores.length === 0) return 0;
  const sum = scores.reduce((acc, s) => acc + s, 0);
  return Math.round((sum / scores.length) * 10) / 10;
}

/**
 * 점수에 따른 색상 반환
 * 1~2: 빨강, 2.1~3: 주황, 3.1~4: 파랑, 4.1~5: 초록
 */
export function getScoreColor(score: number): string {
  if (score <= 2) return '#F44336';
  if (score <= 3) return '#FF9800';
  if (score <= 4) return '#2196F3';
  return '#4CAF50';
}

/**
 * 점수에 따른 이모지 반환
 */
export function getScoreEmoji(score: number): string {
  if (score <= 1.5) return '😢';
  if (score <= 2.5) return '🙁';
  if (score <= 3.5) return '😐';
  if (score <= 4.5) return '🙂';
  return '😀';
}

/**
 * 점수에 따른 한국어 라벨 반환
 */
export function getScoreLabel(score: number): string {
  if (score <= 1.5) return '많이 힘들었어요';
  if (score <= 2.5) return '힘들었어요';
  if (score <= 3.5) return '보통이에요';
  if (score <= 4.5) return '좋아요';
  return '아주 좋아요';
}

/**
 * 알림 조건 체크
 * 현재 체크인과 이전 체크인들을 비교하여 트리거할 알림 유형 반환
 *
 * 6가지 조건:
 * 1. score_drop: 전일 대비 평균점수 2점 이상 하락
 * 2. low_score: 오늘 평균점수 2.5 이하
 * 3. consecutive_low: 2일 연속 평균 2.5 이하
 * 4. consecutive_drop: 3일 연속 점수 하락
 * 5. need_help: q5_need_help <= 2
 * 6. has_message: message_to_admin이 비어있지 않음
 */
export function checkAlertConditions(
  currentCheckin: DailyCheckin,
  previousCheckins: DailyCheckin[]
): AlertType[] {
  const alerts: AlertType[] = [];

  // 이전 체크인을 날짜 내림차순 정렬 (가장 최근이 [0])
  const sorted = [...previousCheckins].sort(
    (a, b) => b.checkin_date.localeCompare(a.checkin_date)
  );

  const currentAvg = currentCheckin.average_score;

  // 1. 전일 대비 점수 하락 >= 2
  if (sorted.length > 0) {
    const yesterdayAvg = sorted[0].average_score;
    if (yesterdayAvg - currentAvg >= 2) {
      alerts.push('score_drop');
    }
  }

  // 2. 오늘 평균 <= 2.5
  if (currentAvg <= 2.5) {
    alerts.push('low_score');
  }

  // 3. 2일 연속 평균 <= 2.5 (오늘 포함)
  if (currentAvg <= 2.5 && sorted.length > 0 && sorted[0].average_score <= 2.5) {
    alerts.push('consecutive_low');
  }

  // 4. 3일 연속 점수 하락 (오늘 포함 최근 3일)
  if (sorted.length >= 2) {
    const day1 = sorted[0].average_score; // 어제
    const day2 = sorted[1].average_score; // 그저께
    if (currentAvg < day1 && day1 < day2) {
      alerts.push('consecutive_drop');
    }
  }

  // 5. 도움 필요 점수 <= 2
  if (currentCheckin.q5_need_help <= 2) {
    alerts.push('need_help');
  }

  // 6. 관리자에게 메시지가 있는 경우
  if (currentCheckin.message_to_admin && currentCheckin.message_to_admin.trim() !== '') {
    alerts.push('has_message');
  }

  return alerts;
}

/** 질문 키에서 한국어 카테고리명 반환 */
function getQuestionLabel(key: string): string {
  const question = CHECKIN_QUESTIONS.find((q) => q.key === key);
  // 질문 라벨에서 핵심 키워드 추출
  const labelMap: Record<string, string> = {
    q1_mood: '기분',
    q2_work: '업무',
    q3_relationship: '대인관계',
    q4_health: '건강',
    q5_need_help: '도움 필요도',
    q6_overall: '전체 만족도',
  };
  return labelMap[key] || question?.label || key;
}

/** 월간 요약 결과 타입 */
export interface MonthSummary {
  totalDays: number;
  averageScore: number;
  bestCategory: string;
  worstCategory: string;
  helpNeededDays: number;
  messageDays: number;
  summaryText: string;
}

/**
 * 월간 체크인 요약 생성
 */
export function getMonthSummary(checkins: DailyCheckin[]): MonthSummary {
  if (checkins.length === 0) {
    return {
      totalDays: 0,
      averageScore: 0,
      bestCategory: '-',
      worstCategory: '-',
      helpNeededDays: 0,
      messageDays: 0,
      summaryText: '이번 달 체크인 기록이 없습니다.',
    };
  }

  const totalDays = checkins.length;

  // 전체 평균 점수
  const averageScore = calculateAverage(checkins.map((c) => c.average_score));

  // 카테고리별 평균 계산
  const questionKeys = ['q1_mood', 'q2_work', 'q3_relationship', 'q4_health', 'q5_need_help', 'q6_overall'] as const;
  const categoryAverages: Record<string, number> = {};

  for (const key of questionKeys) {
    const scores = checkins.map((c) => c[key]);
    categoryAverages[key] = calculateAverage(scores);
  }

  // 최고/최저 카테고리 찾기
  let bestKey: typeof questionKeys[number] = questionKeys[0];
  let worstKey: typeof questionKeys[number] = questionKeys[0];

  for (const key of questionKeys) {
    if (categoryAverages[key] > categoryAverages[bestKey]) bestKey = key;
    if (categoryAverages[key] < categoryAverages[worstKey]) worstKey = key;
  }

  const bestCategory = getQuestionLabel(bestKey);
  const worstCategory = getQuestionLabel(worstKey);

  // 도움 필요 일수 (q5 <= 2)
  const helpNeededDays = checkins.filter((c) => c.q5_need_help <= 2).length;

  // 메시지 작성 일수
  const messageDays = checkins.filter(
    (c) => c.message_to_admin && c.message_to_admin.trim() !== ''
  ).length;

  // 요약 문장 생성
  const scoreLabel = getScoreLabel(averageScore);
  const summaryParts: string[] = [
    `이번 달 ${totalDays}일 체크인, 평균 ${averageScore}점(${scoreLabel}).`,
  ];

  if (bestCategory !== worstCategory) {
    summaryParts.push(`'${bestCategory}' 항목이 가장 좋았고, '${worstCategory}' 항목이 가장 낮았습니다.`);
  }

  if (helpNeededDays > 0) {
    summaryParts.push(`${helpNeededDays}일은 도움이 필요했습니다.`);
  }

  if (messageDays > 0) {
    summaryParts.push(`${messageDays}일은 관리자에게 메시지를 보냈습니다.`);
  }

  const summaryText = summaryParts.join(' ');

  return {
    totalDays,
    averageScore,
    bestCategory,
    worstCategory,
    helpNeededDays,
    messageDays,
    summaryText,
  };
}
